import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, Settings, Copy, Check, Plus, Trash2, Users, Building2, Shield, X, AlertTriangle, AlertCircle } from 'lucide-react';
import { doc, updateDoc, getDoc, collection, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db, joinAgency, deleteAccount, deleteUserFromFirestore } from '../lib/firebase';
import { Dialog } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import ThemeToggle from '../components/ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';

interface Agency {
  id: string;
  name: string;
  createdAt: { seconds: number };
  updatedAt: { seconds: number };
}

interface TeamMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  userRole: 'admin' | 'user';
  permissions?: {
    collaborations: boolean;
    todo: boolean;
    clients: boolean;
    influencers: boolean;
    casting: boolean;
    accounting: boolean;
    resources: boolean;
    assistant: boolean;
  };
  createdAt: Date;
}

export default function Account() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { currentUser, userProfile, isAdmin } = useAuth();
  const [isProfileEditing, setIsProfileEditing] = useState(false);
  const [isAgencyEditing, setIsAgencyEditing] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [agencyName, setAgencyName] = useState('');
  const [agency, setAgency] = useState<Agency | null>(null);
  const [agencyCode, setAgencyCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    role: '',
  });
  const [permissionsData, setPermissionsData] = useState<Record<string, boolean>>({});
  const [isLoadingData, setIsLoadingData] = useState(false);

  useEffect(() => {
    const fetchAgencyDetails = async () => {
      if (userProfile?.agencyId) {
        try {
          setIsLoadingData(true);
          const agencyRef = doc(db, 'agencies', userProfile.agencyId);
          const agencyDoc = await getDoc(agencyRef);
          
          if (agencyDoc.exists()) {
            setAgency({
              id: agencyDoc.id,
              ...agencyDoc.data(),
            } as Agency);
            setAgencyName(agencyDoc.data().name);
          }

          // Fetch team members
          const usersRef = collection(db, 'users');
          const teamQuery = query(usersRef, where('agencyId', '==', userProfile.agencyId));
          const teamSnapshot = await getDocs(teamQuery);
          const teamData = teamSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as TeamMember[];
          setTeamMembers(teamData);
        } catch (error) {
          console.error('Error fetching agency details:', error);
          setError('Une erreur est survenue lors de la récupération des détails de l\'agence.');
        } finally {
          setIsLoadingData(false);
        }
      }
    };

    fetchAgencyDetails();
  }, [userProfile?.agencyId]);

  const loadData = async () => {
    if (userProfile?.agencyId) {
      try {
        setIsLoadingData(true);
        const usersRef = collection(db, 'users');
        const teamQuery = query(usersRef, where('agencyId', '==', userProfile.agencyId));
        const teamSnapshot = await getDocs(teamQuery);
        const teamData = teamSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as TeamMember[];
        setTeamMembers(teamData);
      } catch (error) {
        console.error('Error loading team data:', error);
      } finally {
        setIsLoadingData(false);
      }
    }
  };

  useEffect(() => {
    if (userProfile) {
      setFormData({
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        role: userProfile.role || '',
      });
    }
  }, [userProfile]);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (!currentUser?.uid) return;

      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        updatedAt: serverTimestamp()
      });

      setSuccess('Vos informations ont été mises à jour avec succès.');
      setIsProfileEditing(false);
      
      window.location.reload();
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Une erreur est survenue lors de la mise à jour.');
    }
  };

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await deleteAccount(deletePassword);
      setIsDeleteModalOpen(false);
      navigate('/login');
    } catch (error: any) {
      setError(error.message || 'Une erreur est survenue lors de la suppression du compte.');
    }
  };

  const handleUpdateAgency = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (!userProfile?.agencyId) return;

      const agencyRef = doc(db, 'agencies', userProfile.agencyId);
      await updateDoc(agencyRef, {
        name: agencyName,
        updatedAt: serverTimestamp()
      });

      // Mettre à jour l'état local
      setAgency(prev => prev ? { ...prev, name: agencyName } : null);
      setSuccess('Les informations ont été mises à jour avec succès.');
      setIsAgencyEditing(false);
    } catch (error) {
      setError('Une erreur est survenue lors de la mise à jour.');
    }
  };

  const handleJoinAgency = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await joinAgency(agencyCode);
      setSuccess('Vous avez rejoint l\'agence avec succès.');
      setIsJoining(false);
      window.location.reload();
    } catch (error: any) {
      setError(error.message || 'Une erreur est survenue lors de la tentative de rejoindre l\'agence.');
    }
  };

  const copyAgencyId = async () => {
    if (agency?.id) {
      await navigator.clipboard.writeText(agency.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedMember || !selectedMember.id) {
      setError('Aucun utilisateur sélectionné ou ID manquant');
      return;
    }
    
    try {
      // Utiliser la fonction deleteUser spécialisée
      await deleteUserFromFirestore(selectedMember.id);
      
      // Mettre à jour la liste des membres
      await loadData();
      
      // Fermer le modal et réinitialiser l'utilisateur sélectionné
      setIsDeleteUserModalOpen(false);
      setSelectedMember(null);
      
      // Afficher un message de succès
      setSuccessMessage(`Le compte de ${selectedMember.firstName} ${selectedMember.lastName} a été supprimé avec succès.`);
      
      // Masquer le message après 3 secondes
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Une erreur est survenue lors de la suppression du compte.');
    }
  };

  const formatDate = (date: Date | { seconds: number } | undefined) => {
    if (!date) return '';
    if (typeof date === 'object' && 'seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
          {successMessage}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center">
            <Settings className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Compte</h1>
            <p className="text-gray-600">Gérez vos paramètres personnels</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <button
            onClick={() => setIsDeleteModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
          >
            <Trash2 className="w-4 h-4" />
            Supprimer mon compte
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6 space-y-6">
          {/* User Information */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Informations personnelles</h2>
              <button
                onClick={() => setIsProfileEditing(true)}
                className="text-sm text-indigo-600 hover:text-indigo-700"
              >
                Modifier
              </button>
            </div>

            {isProfileEditing ? (
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}
                {success && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                    {success}
                  </div>
                )}

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                      Prénom
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="mt-1 block w-full rounded-lg bg-white border-gray-300 text-gray-900"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                      Nom
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="mt-1 block w-full rounded-lg bg-white border-gray-300 text-gray-900"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                    Rôle dans l'entreprise
                  </label>
                  <input
                    type="text"
                    id="role"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="mt-1 block w-full rounded-lg bg-white border-gray-300 text-gray-900"
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setIsProfileEditing(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700"
                  >
                    Sauvegarder
                  </button>
                </div>
              </form>
            ) : (
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-4">
                  <div className="bg-indigo-100 p-3 rounded-full">
                    <User className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-900">
                      {userProfile?.firstName} {userProfile?.lastName}
                    </p>
                    <p className="text-sm text-gray-600">{currentUser?.email}</p>
                    <p className="text-sm text-gray-600">
                      {userProfile?.role || 'Rôle non défini'}
                    </p>
                    <p className="text-sm text-gray-600">
                      Type de compte: {userProfile?.userRole === 'admin' || agency?.ownerId === currentUser?.uid ? 'Administrateur' : 'Utilisateur'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Agency Information */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Informations de l'agence</h2>
              {!userProfile?.agencyId && (
                <button
                  onClick={() => setIsJoining(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                >
                  <Plus className="w-4 h-4" />
                  Rejoindre une agence
                </button>
              )}
            </div>
            
            {isJoining ? (
              <form onSubmit={handleJoinAgency} className="space-y-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}
                {success && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                    {success}
                  </div>
                )}
                
                <div>
                  <label htmlFor="agencyCode" className="block text-sm font-medium text-gray-700">
                    Code de l'agence
                  </label>
                  <input
                    type="text"
                    id="agencyCode"
                    value={agencyCode}
                    onChange={(e) => setAgencyCode(e.target.value)}
                    className="mt-1 block w-full rounded-lg bg-white border-2 border-black text-gray-900"
                    required
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setIsJoining(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700"
                  >
                    Rejoindre
                  </button>
                </div>
              </form>
            ) : (
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-4">
                {userProfile?.agencyId ? (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-indigo-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Nom de l'agence</p>
                          <p className="text-sm text-gray-600">{agency?.name || 'Non défini'}</p>
                        </div>
                      </div>
                      <div className="mt-3">
                        <p className="text-sm font-medium text-gray-900">Date de création</p>
                        <p className="text-sm text-gray-600">
                          {agency?.createdAt ? formatDate(agency.createdAt) : 'Non défini'}
                        </p>
                      </div>
                    </div>
                    {userProfile?.userRole === 'admin' && (
                      <button
                        onClick={() => setIsAgencyEditing(true)}
                        className="text-sm text-indigo-600 hover:text-indigo-700"
                      >
                        Modifier
                      </button>
                    )}

                    {isAgencyEditing ? (
                      <form onSubmit={handleUpdateAgency} className="space-y-4 pt-4 border-t border-gray-200">
                        <div>
                          <label htmlFor="agencyName" className="block text-sm font-medium text-gray-700">
                            Nom de l'agence
                          </label>
                           <input
                            type="text"
                            id="agencyName"
                            value={agencyName}
                            onChange={(e) => setAgencyName(e.target.value)}
                            className="mt-1 block w-full rounded-lg bg-white border-gray-300 text-gray-900"
                            required
                          />
                        </div>

                        <div className="flex justify-end space-x-3">
                          <button
                            type="button"
                            onClick={() => setIsAgencyEditing(false)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            Annuler
                          </button>
                          <button
                            type="submit"
                            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700"
                          >
                            Sauvegarder
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div className="pt-4 border-t border-gray-200">
                        <p className="text-sm font-medium text-gray-900 mb-2">Code de l'agence</p>
                        <div className="flex items-center justify-between bg-gray-100 p-3 rounded-lg border border-gray-200">
                          <code className="text-sm text-gray-600">{agency?.id}</code>
                          <button
                            onClick={copyAgencyId}
                            className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700"
                          >
                            {copied ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                            {copied ? 'Copié !' : 'Copier'}
                          </button>
                        </div>
                        <p className="mt-2 text-sm text-gray-600">
                          Partagez ce code avec les membres de votre équipe pour qu'ils puissent rejoindre votre agence.
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-gray-600">Vous n'êtes pas encore membre d'une agence.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Team Members */}
          {userProfile?.agencyId && teamMembers.length > 0 && (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Membres de l'équipe</h2>
                <div className="bg-gray-100 px-3 py-1 rounded-lg text-sm text-gray-600 border border-gray-200">
                  {teamMembers.length} membre{teamMembers.length > 1 ? 's' : ''}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg divide-y divide-gray-200 border border-gray-200">
                {teamMembers.map((member) => (
                  <div 
                    key={member.id} 
                    className={`p-4 ${agency?.ownerId === userProfile?.id ? 'cursor-pointer hover:bg-gray-100' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      if ((agency?.ownerId === currentUser?.uid || userProfile?.userRole === 'admin') && member.id !== currentUser?.uid) {
                        setSelectedMember(member);
                        setPermissionsData(member.permissions || {
                          collaborations: false,
                          todo: false,
                          clients: false,
                          influencers: false,
                          casting: false,
                          accounting: false,
                          resources: false,
                          assistant: false,
                          admin: false
                        });
                        setIsPermissionsModalOpen(true);
                      }
                    }}
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <User className="w-5 h-5 text-indigo-600" />
                          <div>
                            <span className="text-gray-900 font-medium">{member.firstName} {member.lastName}</span>
                            <p className="text-sm text-gray-500">{member.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">{member.role || 'Rôle non défini'}</p>
                          <span className={`inline-flex items-center px-2 py-1 mt-1 rounded-full text-xs font-medium ${
                            member.userRole === 'admin' 
                              ? 'bg-indigo-100 text-indigo-600' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {member.userRole === 'admin' ? 'Administrateur' : 'Utilisateur'}
                          </span>
                        </div>
                      </div>
                      
                      {(agency?.ownerId === currentUser?.uid || userProfile?.userRole === 'admin') && member.id !== currentUser?.uid && (
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedMember(member);
                              setPermissionsData(member.permissions || {
                                collaborations: false,
                                todo: false,
                                clients: false,
                                influencers: false,
                                casting: false,
                                accounting: false,
                                resources: false,
                                assistant: false,
                                admin: false
                              });
                              setIsPermissionsModalOpen(true);
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors"
                          >
                            <Shield className="w-4 h-4" />
                            <span className="text-sm">Gérer les permissions</span>
                          </button>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedMember(member);
                              setIsDeleteUserModalOpen(true);
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span className="text-sm">Supprimer</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Account Modal */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />

          <div className="relative bg-white rounded-lg max-w-md w-full mx-4 p-6 border border-gray-200 shadow-lg">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900">Supprimer le compte</h3>
              <p className="mt-2 text-sm text-gray-600">
                Cette action est irréversible. Toutes vos données seront définitivement supprimées.
              </p>
            </div>

            <form onSubmit={handleDeleteAccount} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Confirmez votre mot de passe
                </label>
                <input
                  type="password"
                  id="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="mt-1 block w-full rounded-lg bg-white border-gray-300 text-gray-900"
                  required
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700"
                >
                  Supprimer définitivement
                </button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>

      {/* Permissions Modal */}
      <Dialog
        open={isPermissionsModalOpen}
        onClose={() => setIsPermissionsModalOpen(false)}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />

          <div className="relative bg-white rounded-lg max-w-md w-full mx-4 p-6 border border-gray-200 shadow-lg">
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Permissions utilisateur</h3>
                <button
                  onClick={() => setIsPermissionsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {selectedMember && (
                <p className="mt-2 text-sm text-gray-600">
                  Gérer les permissions pour {selectedMember.firstName} {selectedMember.lastName}
                </p>
              )}
            </div>

            <div className="space-y-4 max-h-96 overflow-y-auto">
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-indigo-600" />
                    <span className="font-medium text-gray-900">Administrateur</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer"
                      checked={permissionsData.admin || false}
                      onChange={(e) => {
                        setPermissionsData(prev => ({
                          ...prev,
                          admin: e.target.checked
                        }));
                      }}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
                <p className="text-sm text-gray-600">
                  Donne accès à toutes les fonctionnalités et à la gestion des utilisateurs
                </p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-medium text-gray-900 mb-3">Modules</h4>
                <div className="space-y-3">
                  {[
                    { key: 'dashboard', label: 'Dashboard', description: 'Accès au tableau de bord' },
                    { key: 'collaborations', label: 'Collaborations', description: 'Gestion des campagnes et collaborations' },
                    { key: 'clients', label: 'Clients', description: 'Gestion des clients et contacts' },
                    { key: 'influencers', label: 'Influenceurs', description: 'Gestion des influenceurs' },
                    { key: 'casting', label: 'Casting', description: 'Gestion des castings' },
                    { key: 'todo', label: 'Tâches', description: 'Gestion des tâches' },
                    { key: 'recruitment', label: 'Recrutement', description: 'Recrutement d\'influenceurs' },
                    { key: 'accounting', label: 'Comptabilité', description: 'Gestion de la comptabilité' },
                    { key: 'resources', label: 'Ressources', description: 'Accès aux ressources' },
                    { key: 'assistant', label: 'Assistant IA', description: 'Accès à l\'assistant IA' }
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-900">{item.label}</p>
                        <p className="text-xs text-gray-500">{item.description}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="sr-only peer"
                          checked={permissionsData[item.key as keyof typeof permissionsData] || false}
                          onChange={(e) => {
                            setPermissionsData(prev => ({
                              ...prev,
                              [item.key]: e.target.checked
                            }));
                          }}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setIsPermissionsModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={async () => {
                  if (selectedMember) {
                    try {
                      const userRef = doc(db, 'users', selectedMember.id);
                      await updateDoc(userRef, {
                        permissions: permissionsData,
                        updatedAt: serverTimestamp()
                      });
                      
                      // Si l'utilisateur est promu admin, mettre à jour son userRole
                      if (permissionsData.admin) {
                        await updateDoc(userRef, {
                          userRole: 'admin',
                          updatedAt: serverTimestamp()
                        });
                      } else if (selectedMember.userRole === 'admin') {
                        // Si l'utilisateur était admin et ne l'est plus
                        await updateDoc(userRef, {
                          userRole: 'user',
                          updatedAt: serverTimestamp()
                        });
                      }
                      
                      await loadData();
                      setIsPermissionsModalOpen(false);
                      setSuccessMessage(`Les permissions de ${selectedMember.firstName} ${selectedMember.lastName} ont été mises à jour.`);
                      
                      // Masquer le message après 3 secondes
                      setTimeout(() => {
                        setSuccessMessage(null);
                      }, 3000);
                    } catch (error) {
                      console.error('Error updating permissions:', error);
                      setError('Une erreur est survenue lors de la mise à jour des permissions.');
                    }
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Delete User Modal */}
      <Dialog
        open={isDeleteUserModalOpen}
        onClose={() => setIsDeleteUserModalOpen(false)}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />

          <div className="relative bg-white rounded-lg max-w-md w-full mx-4 p-6 border border-gray-200 shadow-lg">
            <div className="mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Supprimer l'utilisateur</h3>
                  <p className="text-sm text-gray-600">Cette action est irréversible</p>
                </div>
              </div>
              
              {selectedMember && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-gray-900 font-medium">{selectedMember.firstName} {selectedMember.lastName}</p>
                  <p className="text-sm text-gray-600">{selectedMember.email}</p>
                  <p className="text-sm text-gray-600 mt-2">{selectedMember.role || 'Rôle non défini'}</p>
                </div>
              )}
              
              <p className="mt-4 text-gray-700">
                Êtes-vous sûr de vouloir supprimer cet utilisateur de votre agence ? Cette action est irréversible et supprimera définitivement l'accès de cet utilisateur à votre agence.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsDeleteUserModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteUser}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700"
              >
                Supprimer l'utilisateur
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}